import React, { useCallback, useState } from 'react';
import classNames from 'classnames';

import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import { showToast } from 'utils/toasts';
import { ToastTypes } from 'common/types/toasts';
import { track } from 'utils/kitAnalytics';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import { addKitLine } from 'containers/KitWorkspaceStorage/api';
import { MAX_LINES_PER_KIT, MIN_QTY, MAX_QTY } from 'containers/KitWorkspaceStorage/types';
import type { IAssembly } from 'containers/KitWorkspaceStorage/types';
import { assemblyDisplayName, buildSpecSummary, assemblyQty } from 'common/components/kit-shared/assemblySummary';

import Button from 'common/components/Button';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import BottomSheet from 'common/components/kit-shared/BottomSheet';
import QtyStepper from 'common/components/kit-shared/QtyStepper';
import KitFormDialog from 'common/components/kit-shared/KitFormDialog';
import styles from './KitPicker.module.scss';

interface IKitPickerProps {
  workspaceKey: string;
  assembly: IAssembly;
  qty?: number;
  onClose: () => void;
  onAdded?: () => void;
}

/**
 * Centered "Add to kit" picker: pick a kit (or create one via "+ New kit"), set
 * the quantity to add, then add the assembly as a kit line. Each assembly can
 * belong to at most one kit, so full kits are disabled.
 */
const KitPicker = ({
  workspaceKey, assembly, qty: initialQty, onClose, onAdded,
}: IKitPickerProps) => {
  const t = useKitTranslate();
  const { kits, refetch } = useKitWorkspace();
  const assemblyName = assemblyDisplayName(assembly) || t('texts.kits.untitledName');
  const assemblySpec = buildSpecSummary(assembly);
  const [selectedKitId, setSelectedKitId] = useState<string | null>(
    kits.length ? kits[kits.length - 1].uuid : null,
  );
  // Seed the kit-line qty from the assembly's configured Assembly Qty (set in the
  // wizard) so adding to a kit keeps that quantity instead of resetting to 1.
  // Clamp to the kit-line bounds.
  const seededQty = Math.min(MAX_QTY, Math.max(MIN_QTY, initialQty ?? assemblyQty(assembly)));
  const [qty, setQty] = useState(seededQty);
  const [busy, setBusy] = useState(false);
  const [showNewKit, setShowNewKit] = useState(false);

  const selectedKit = kits.find((k) => k.uuid === selectedKitId) || null;
  const selectedFull = !!selectedKit && selectedKit.lines.length >= MAX_LINES_PER_KIT;

  const onAdd = useCallback(async () => {
    if (!selectedKitId || selectedFull) return;
    setBusy(true);
    try {
      await addKitLine(selectedKitId, { assemblyUuid: assembly.uuid, qty });
      track('kit_line_added', {
        workspaceKey, kitUuid: selectedKitId, assemblyUuid: assembly.uuid, qty,
      });
      showToast(ToastTypes.success, t('texts.kits.toast.lineAdded'));
      await refetch();
      if (onAdded) onAdded();
      onClose();
    } catch (e: any) {
      if (e?.status === 400) {
        showToast(ToastTypes.warning, t('texts.kits.toast.limitReached', { limit: MAX_LINES_PER_KIT }));
      } else {
        showToast(ToastTypes.error, t('texts.kits.toast.updateError'));
      }
      setBusy(false);
    }
  }, [selectedKitId, selectedFull, workspaceKey, assembly.uuid, qty, refetch, onAdded, onClose, t]);

  return (
    <>
      <BottomSheet
        title={t('texts.kits.picker.title')}
        subtitle={assemblySpec ? `${assemblyName} · ${assemblySpec}` : assemblyName}
        onClose={onClose}
        centered
        closeDisabled={busy}
        footer={(
          <>
            <Button
              schema={ButtonSchema.secondary}
              size={ButtonSize.small}
              onClick={onClose}
              disabled={busy}
            >
              {t('texts.kits.catalog.cancel')}
            </Button>
            <Button
              schema={ButtonSchema.primary}
              size={ButtonSize.small}
              onClick={onAdd}
              disabled={busy || !selectedKitId || selectedFull}
            >
              {t('texts.kits.picker.addBtn')}
            </Button>
          </>
        )}
      >
        {kits.length === 0 ? (
          <p className={styles.empty}>{t('texts.kits.picker.empty')}</p>
        ) : (
          <div className={styles.field}>
            <div className={styles.label}>{t('texts.kits.picker.pickLabel')}</div>
            <div className={styles.kitList}>
              {kits.map((kit) => {
                const full = kit.lines.length >= MAX_LINES_PER_KIT;
                const selected = kit.uuid === selectedKitId;
                return (
                  <label
                    key={kit.uuid}
                    htmlFor={kit.uuid}
                    className={classNames(styles.kitRow, {
                      [styles.kitRowSelected]: selected,
                      [styles.kitRowDisabled]: full,
                    })}
                  >
                    <input
                      type="radio"
                      id={kit.uuid}
                      name="kit-sel"
                      className={styles.radio}
                      checked={selected}
                      disabled={full || busy}
                      onChange={() => setSelectedKitId(kit.uuid)}
                    />
                    <span className={styles.kitName}>
                      {kit.name || <span className={styles.pill}>{t('texts.kits.pills.untitled')}</span>}
                    </span>
                    <span className={styles.kitMeta}>
                      {t('texts.kits.picker.lineCount', { count: kit.lines.length })}
                    </span>
                    {full && <span className={styles.warnPill}>{t('texts.kits.picker.full')}</span>}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <Button
          schema={ButtonSchema.secondary}
          size={ButtonSize.full}
          className={styles.newKitBtn}
          onClick={() => setShowNewKit(true)}
          disabled={busy}
        >
          {t('texts.kits.picker.newKitBtn')}
        </Button>

        {selectedKitId && !selectedFull && (
          <div className={styles.field}>
            <div className={styles.label}>{t('texts.kits.picker.qtyLabel')}</div>
            <QtyStepper value={qty} onChange={setQty} disabled={busy} />
          </div>
        )}
      </BottomSheet>

      {showNewKit && (
        <KitFormDialog
          workspaceKey={workspaceKey}
          onCancel={() => setShowNewKit(false)}
          onSaved={(kitId) => { setSelectedKitId(kitId); setShowNewKit(false); }}
        />
      )}
    </>
  );
};

export default KitPicker;
